"use client";

import { StarIcon as OutlineStarIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import { useRouter } from "next/navigation";
import { ComponentProps } from "react";
import { toast } from "react-hot-toast";
import { twMerge } from "tailwind-merge";

import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useActiveWatchlist } from "@/features/watchlist/services/get-active-watchlist";
import { useUpdateActiveWatchlist } from "@/features/watchlist/services/update-active-watchlist";
import { useLang } from "@/hooks/use-lang";

import { Button } from "ui/core/button";

type Props = ComponentProps<typeof Button> & {
  symbol: string;
};

export const DesktopFollowButton = ({ symbol, ...props }: Props) => {
  const router = useRouter();
  const { t } = useLang();
  const { isUnauthenticated } = useAuth();
  const { data: activeWatchlist } = useActiveWatchlist();
  const symbols = activeWatchlist?.symbols ?? [];
  const symbolExists = symbols.includes(symbol);
  const { mutate: updateWatchlist } = useUpdateActiveWatchlist();

  const toggleSymbolInWatchlist = () => {
    const newSymbols = symbolExists
      ? symbols.filter((s) => s !== symbol)
      : symbols.concat(symbol);

    updateWatchlist(
      { symbols: newSymbols },
      {
        onSuccess: () => {
          toast.success(
            symbolExists
              ? t.layout.watchlist.removeSymbolSuccessToast(symbol)
              : t.layout.watchlist.addSymbolSuccessToast(symbol)
          );
        },
        onError: () => {
          toast.error(t.error.serverError);
        },
      }
    );
  };

  return (
    <Button
      variant="secondary"
      onClick={(e) => {
        e.preventDefault();

        if (isUnauthenticated) return router.push(links.authSignup);
        toggleSymbolInWatchlist();
      }}
      {...props}
      className={twMerge(
        "flex w-40 shrink-0 items-center justify-center gap-2 whitespace-nowrap px-0",
        props.className
      )}
    >
      {symbolExists ? (
        <StarIcon className="h-5 w-5 fill-yellow-400" />
      ) : (
        <OutlineStarIcon className="h-5 w-5" />
      )}
      {symbolExists
        ? t.layout.watchlist.addedToWatchlist
        : t.layout.watchlist.addToWatchlist}
    </Button>
  );
};
